/**
 * WhatsApp — the chat page (owner, 29 October 2026: "an internal chat box,
 * otherwise I don't understand how they will chat").
 *
 * Every conversation with a customer in one place, like WhatsApp itself: the
 * numbers on the left (those waiting for an answer first, with a badge), the
 * conversation on the right, the answer box under it. Opened from the menu
 * item "WhatsApp" (the owner's word, 29 Oct 2026) or from the 💬 button in the
 * top bar ("Všechny chaty", or a message in it).
 *
 * The rules are WhatsApp's, not ours: a free answer only within 24 hours after
 * the customer last wrote (after that: call or e-mail), and only in mode
 * "send". A number we cannot tie to a booking (a customer writing from another
 * phone) is "Neznámé číslo"; the desk puts it on the booking with
 * "Přiřadit k rezervaci", and the booking card then shows the conversation.
 */
import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, api, type WaChat, type WaMessage } from '../api/client';

/* Translation (owner, 31 Oct 2026: the staff do not speak German or Polish). Each dispatcher picks the
   language the customers' messages are shown in; the browser remembers it. */
const LANG_NAME: Record<string, string> = {
  cs: 'čeština', ru: 'ruština', uk: 'ukrajinština', sk: 'slovenština', de: 'němčina', pl: 'polština', en: 'angličtina',
  fr: 'francouzština', it: 'italština', es: 'španělština', nl: 'nizozemština', hu: 'maďarština', ro: 'rumunština',
  bg: 'bulharština', lt: 'litevština', lv: 'lotyština', et: 'estonština', sl: 'slovinština', hr: 'chorvatština',
  sr: 'srbština', tr: 'turečtina', pt: 'portugalština', da: 'dánština', sv: 'švédština', no: 'norština',
  fi: 'finština', el: 'řečtina', he: 'hebrejština', ar: 'arabština', zh: 'čínština', ja: 'japonština',
  ko: 'korejština', vi: 'vietnamština', uz: 'uzbečtina', kk: 'kazaština',
};
const MY_LANGS: Array<[string, string]> = [
  ['ru', 'Русский'],
  ['cs', 'Čeština'],
  ['off', 'bez překladu'],
];
const LANG_KEY = 'wa-translate-to';
function readLang(): string {
  try {
    const v = localStorage.getItem(LANG_KEY);
    if (v && MY_LANGS.some(([k]) => k === v)) return v;
  } catch {
    /* private window: the default */
  }
  return 'ru';
}
function writeLang(v: string): void {
  try {
    localStorage.setItem(LANG_KEY, v);
  } catch {
    /* not remembered — fine */
  }
}
const langName = (l: string) => LANG_NAME[l] ?? l.toUpperCase();

const STATUS_LABEL: Record<string, string> = {
  sent: '✓ odesláno',
  delivered: '✓✓ doručeno',
  read: '✓✓ přečteno',
  failed: '✗ neodešlo',
  logged: '· jen zapsáno',
  received: '',
};
const AUTO_LABEL: Record<string, string> = {
  confirmation: 'Automaticky: potvrzení rezervace',
  arrival: 'Automaticky: připomínka příjezdu',
  departure: 'Automaticky: připomínka vyzvednutí',
  flight: 'Automaticky: dotaz na číslo letu',
};

function when(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  return new Intl.DateTimeFormat('cs-CZ', sameDay ? { timeZone: 'Europe/Prague', hour: '2-digit', minute: '2-digit' } : { timeZone: 'Europe/Prague', day: 'numeric', month: 'numeric', hour: '2-digit', minute: '2-digit' }).format(d);
}
function errText(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  return e instanceof Error ? e.message : String(e);
}
function autoLabel(kind: string): string | null {
  if (AUTO_LABEL[kind]) return AUTO_LABEL[kind];
  return kind.startsWith('template') ? 'Automatická zpráva' : null;
}

function ChatRow({ c, active, onPick }: { c: WaChat; active: boolean; onPick: () => void }) {
  return (
    <li>
      <button type="button" className={`wa-chatrow${active ? ' on' : ''}${c.waiting ? ' waiting' : ''}`} onClick={onPick}>
        <span className="wa-chatrow-top">
          <b>{c.name || (c.reservationId ? `Rezervace ${c.reservationId}` : 'Neznámé číslo')}</b>
          <span className="wa-muted">{when(c.at)}</span>
        </span>
        <span className="wa-chatrow-mid">
          <span className="wa-muted">
            {c.phoneShown}
            {c.reservationId ? ` · č. ${c.reservationId}` : ''}
          </span>
          {c.waiting > 0 && <span className="wa-badge">{c.waiting}</span>}
        </span>
        <span className="wa-chatrow-last">
          {c.direction === 'out' ? 'Vy: ' : ''}
          {c.body || '—'}
        </span>
      </button>
    </li>
  );
}

function Conversation({ phone, mode, canWrite }: { phone: string; mode: string; canWrite: boolean }) {
  const qc = useQueryClient();
  const key = ['whatsapp-chat', phone];
  const q = useQuery({ queryKey: key, queryFn: () => api.whatsAppChat(phone), refetchInterval: 15_000 });
  const [text, setText] = useState('');
  const [linkTo, setLinkTo] = useState('');
  const [myLang, setMyLang] = useState(readLang);
  /** The dispatcher's own text before "Přeložit" put the customer's language in the box. */
  const [original, setOriginal] = useState<string | null>(null);
  const [custLangPick, setCustLangPick] = useState<string>('');
  const endRef = useRef<HTMLDivElement>(null);
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: key });
    void qc.invalidateQueries({ queryKey: ['whatsapp-chats'] });
    void qc.invalidateQueries({ queryKey: ['whatsapp-inbox'] });
  };
  const send = useMutation({
    mutationFn: () => api.whatsAppChatReply(phone, text.trim()),
    onSuccess: () => {
      setText('');
      setOriginal(null);
      refresh();
    },
  });
  const canTranslate = !!q.data?.translate;
  const inIds = (q.data?.messages ?? []).filter((m) => m.direction === 'in' && m.kind !== 'dismissed' && m.body.trim()).map((m) => m.id);
  const tr = useQuery({
    queryKey: ['whatsapp-translate', phone, myLang, inIds.join(',')],
    queryFn: () => api.whatsAppTranslate(phone, inIds, myLang),
    enabled: canTranslate && myLang !== 'off' && inIds.length > 0,
    staleTime: Infinity,
    retry: 1,
  });
  // The customer's language: what the translator found in their latest message, else the number's country.
  const lastSource = [...inIds].reverse().map((id) => tr.data?.translations[String(id)]?.source).find((s) => s && LANG_NAME[s]);
  const custLang = custLangPick || lastSource || q.data?.phoneLang || 'en';
  const draft = useMutation({
    mutationFn: () => api.whatsAppTranslateDraft(text.trim(), custLang),
    onSuccess: (r) => {
      setOriginal(text.trim());
      setText(r.text);
    },
  });
  const dismiss = useMutation({ mutationFn: () => api.whatsAppDismiss(phone), onSuccess: refresh });
  const link = useMutation({
    mutationFn: () => api.whatsAppChatLink(phone, Number(linkTo)),
    onSuccess: () => {
      setLinkTo('');
      refresh();
    },
  });
  const count = q.data?.messages.length ?? 0;
  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'end' });
  }, [count, phone]);

  if (q.isPending) return <div className="empty">Načítám…</div>;
  if (q.isError) return <div className="empty">Nelze načíst: {errText(q.error)}</div>;
  const d = q.data;
  const shown = d.messages;
  // Waiting = the customer wrote last; a desk reply or "Vyřízeno" after it answers it (as the bell counts).
  const lastAnswer = d.messages.reduce((n, m, i) => (m.direction === 'out' && ((m.kind === 'text' && m.status !== 'failed') || m.kind === 'dismissed') ? i : n), -1);
  const lastIn = d.messages.reduce((n, m, i) => (m.direction === 'in' ? i : n), -1);
  const waiting = lastIn > lastAnswer;

  return (
    <>
      <div className="wa-convhead">
        <div>
          <b>{d.name || (d.reservationId ? `Rezervace ${d.reservationId}` : 'Neznámé číslo')}</b>{' '}
          <span className="wa-muted">{d.phoneShown}</span>
        </div>
        <div className="wa-convacts">
          {d.reservationId ? (
            <Link className="btn sm" to={`/rezervace/${d.reservationId}`}>
              Otevřít rezervaci {d.reservationId}
            </Link>
          ) : (
            canWrite && (
              <form
                className="wa-link"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (Number(linkTo) > 0) link.mutate();
                }}
              >
                <input inputMode="numeric" value={linkTo} onChange={(e) => setLinkTo(e.target.value.replace(/\D/g, ''))} placeholder="číslo rezervace" aria-label="Číslo rezervace" />
                <button type="submit" className="btn sm" disabled={link.isPending || !linkTo}>
                  Přiřadit k rezervaci
                </button>
              </form>
            )
          )}
          {canTranslate && (
            <label className="wa-lang" title="Do jakého jazyka překládat zprávy zákazníků (pamatuje si tento prohlížeč)">
              Překlad:{' '}
              <select
                value={myLang}
                onChange={(e) => {
                  setMyLang(e.target.value);
                  writeLang(e.target.value);
                }}
              >
                {MY_LANGS.map(([k, n]) => (
                  <option key={k} value={k}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
          )}
          {waiting && canWrite && (
            <button type="button" className="btn sm" disabled={dismiss.isPending} title="Není co odpovídat (např. „děkuji“)" onClick={() => dismiss.mutate()}>
              Vyřízeno
            </button>
          )}
        </div>
      </div>
      {link.isError && <div className="arrive-error">{errText(link.error)}</div>}
      {tr.isError && <div className="wa-muted">Překlad se teď nepodařil: {errText(tr.error)}</div>}

      <div className="wa-thread wa-convthread">
        {shown.map((m: WaMessage) => {
          if (m.kind === 'dismissed') return <div key={m.id} className="wa-done">✓ Vyřízeno {when(m.at)}</div>;
          const auto = m.direction === 'out' ? autoLabel(m.kind) : null;
          return (
            <div key={m.id} className={m.direction === 'in' ? 'wa-msg in' : `wa-msg out${auto ? ' auto' : ''}`}>
              {auto && <div className="wa-auto">{auto}</div>}
              <div className="wa-body">{m.body}</div>
              {m.direction === 'in' && myLang !== 'off' && (() => {
                const t = tr.data?.translations[String(m.id)];
                if (!t || t.source === myLang || t.text.trim() === m.body.trim()) return null;
                return (
                  <div className="wa-tr" title="Strojový překlad (Google)">
                    🌐 <span className="wa-muted">{t.source ? `${t.source.toUpperCase()} → ` : ''}{myLang.toUpperCase()}:</span> {t.text}
                  </div>
                );
              })()}
              <div className="wa-meta">
                {when(m.at)} {m.direction === 'out' ? STATUS_LABEL[m.status] ?? m.status : ''}
                {m.error ? ` — ${m.error}` : ''}
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>

      {d.windowUntil ? (
        canWrite && mode === 'send' ? (
          <form
            className="wa-reply"
            onSubmit={(e) => {
              e.preventDefault();
              if (text.trim()) send.mutate();
            }}
          >
            {original !== null && (
              <div className="wa-trnote">
                Přeloženo do: <b>{langName(custLang)}</b> — zkontrolujte a odešlete. Váš text: „{original}“{' '}
                <button
                  type="button"
                  className="btn sm"
                  onClick={() => {
                    setText(original);
                    setOriginal(null);
                  }}
                >
                  Vrátit můj text
                </button>
              </div>
            )}
            <textarea
              value={text}
              onChange={(e) => {
                setText(e.target.value);
                if (!e.target.value.trim()) setOriginal(null);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && text.trim()) send.mutate();
              }}
              rows={3}
              maxLength={4000}
              placeholder={`Odpověď zákazníkovi (lze do ${when(d.windowUntil)}) — Ctrl+Enter odešle`}
            />
            <div className="wa-replyacts">
              {canTranslate && original === null && (
                <span className="wa-trbtn">
                  <button type="button" className="btn sm" disabled={draft.isPending || !text.trim()} onClick={() => draft.mutate()} title="Přeloží text v okně do jazyka zákazníka; nic neodešle">
                    {draft.isPending ? 'Překládám…' : `Přeložit → ${langName(custLang)}`}
                  </button>
                  <select value={custLang} onChange={(e) => setCustLangPick(e.target.value)} aria-label="Jazyk zákazníka" title="Jazyk zákazníka (zjištěný z jeho zpráv — lze změnit)">
                    {Object.keys(LANG_NAME).map((l) => (
                      <option key={l} value={l}>
                        {l.toUpperCase()}
                      </option>
                    ))}
                  </select>
                </span>
              )}
              <button type="submit" className="btn sm primary" disabled={send.isPending || !text.trim()}>
                {send.isPending ? 'Odesílám…' : 'Odeslat na WhatsApp'}
              </button>
            </div>
            {draft.isError && <div className="arrive-error">{errText(draft.error)}</div>}
            {send.isError && <div className="arrive-error">{errText(send.error)}</div>}
          </form>
        ) : (
          <div className="wa-muted">{mode !== 'send' ? 'WhatsApp je zatím v režimu bez odesílání — odpovídat půjde po zapnutí.' : 'Odpovídání je na tomto nasazení vypnuté.'}</div>
        )
      ) : (
        <div className="wa-muted">
          {shown.some((m) => m.direction === 'in')
            ? 'Zákazník nepsal posledních 24 hodin — WhatsApp nedovolí volnou odpověď. Zavolejte nebo napište e-mail.'
            : 'Zákazník zatím nenapsal — odpovídat lze až na jeho zprávu (pravidlo WhatsAppu).'}
        </div>
      )}
    </>
  );
}

export function WhatsAppChaty() {
  const [params, setParams] = useSearchParams();
  const selected = (params.get('cislo') ?? '').replace(/\D/g, '');
  const [filter, setFilter] = useState('');
  const q = useQuery({ queryKey: ['whatsapp-chats'], queryFn: () => api.whatsAppChats(), refetchInterval: 20_000 });
  const chats = q.data?.chats ?? [];
  const f = filter.trim().toLowerCase();
  const fd = f.replace(/\D/g, '');
  const shown = f
    ? chats.filter((c) => (c.name ?? '').toLowerCase().includes(f) || (fd && (c.phone.includes(fd) || String(c.reservationId ?? '').includes(fd))))
    : chats;
  const waiting = chats.filter((c) => c.waiting > 0).length;
  const pick = (phone: string) => setParams({ cislo: phone }, { replace: true });

  return (
    <div className="container">
      <div className="pagehead">
        <h1>WhatsApp</h1>
        <span className="sub">
          {q.data ? `${chats.length} ${chats.length === 1 ? 'konverzace' : 'konverzací'}${waiting ? ` · čeká na odpověď: ${waiting}` : ''}` : '…'}
          {q.data && q.data.mode !== 'send' ? ' · režim bez odesílání' : ''}
        </span>
      </div>
      {q.data?.paused ? (
        <div className="card state">
          Hlídač pozastavil automatické zprávy ({new Date(q.data.paused.at).toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' })}): příliš mnoho nedoručených. Zákazníkům teď nic automaticky neodchází — dejte vědět majiteli.
        </div>
      ) : null}
      {q.isError ? (
        <div className="card state">Nelze načíst: {errText(q.error)}</div>
      ) : (
        <div className={`wa-chats${selected ? ' picked' : ''}`}>
          <section className="card wa-chatlist">
            <input type="search" value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Hledat jméno, telefon, rezervaci" aria-label="Hledat" />
            {q.isPending ? (
              <div className="empty">Načítám…</div>
            ) : shown.length === 0 ? (
              <div className="empty">{chats.length ? 'Nic nenalezeno.' : 'Zatím žádné zprávy.'}</div>
            ) : (
              <ul>
                {shown.map((c) => (
                  <ChatRow key={c.phone} c={c} active={c.phone === selected} onPick={() => pick(c.phone)} />
                ))}
              </ul>
            )}
          </section>
          <section className="card wa-conv">
            {selected ? (
              <>
                <button type="button" className="btn sm wa-back" onClick={() => setParams({}, { replace: true })}>
                  ← Všechny chaty
                </button>
                <Conversation key={selected} phone={selected} mode={q.data?.mode ?? 'off'} canWrite={!!q.data?.canWrite} />
              </>
            ) : (
              <div className="empty">Vyberte konverzaci vlevo.</div>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
